import { Button, IconButton, Tooltip } from '@assembly-js/design-system';
import type { CSSProperties } from 'react';

import { HELP_CENTER_PAGE } from 'src/constants';
import { PortalInterceptModal } from 'src/features/crm/portal-intercept-modal/PortalInterceptModal';
import { usePortalInterceptModal } from 'src/features/crm/portal-intercept-modal/usePortalInterceptModal';
import { usePortalHealthStatus } from 'src/hooks/usePortalHealthStatus';
import { UrlUtils } from 'src/utils';

import { useSidebarTheme } from './useSidebarTheme';

interface SidebarBottomBarProps {
  onSettingsClick: () => void;
  onHelpCenterClick: () => void;
  isClient: boolean;
  isHelpCenterOpen: boolean;
  isSettingsSelected: boolean;
}

const TOOLTIP_DELAY = 500;
const tooltipClassname = 'text-xs text-primary py-1 px-2 shadow-tooltip';

const PortalButton = () => {
  const { isPortalReady, clientExperienceLink } = usePortalHealthStatus();
  const { shouldIntercept, isOpen, open, dismiss, goToCrm } =
    usePortalInterceptModal();

  const openPortal = () =>
    window.open(UrlUtils.GetFullUrl(clientExperienceLink), '_blank');

  const handleClick = () => {
    // Before the Explore step is done, interrupt the shortcut and steer the
    // user back through the guided CRM flow rather than opening the portal cold.
    if (shouldIntercept) {
      open();
      return;
    }
    openPortal();
  };

  // "Continue to portal" honors the original shortcut but still records the
  // intercept as seen so it won't nag again.
  const handleContinueToPortal = () => {
    dismiss();
    openPortal();
  };

  return (
    <>
      <Tooltip
        content="Portal is getting setup. Check back in a minute"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        baseOffset={4}
        showDelayDuration={TOOLTIP_DELAY}
        tooltipClassname={tooltipClassname}
        disabled={isPortalReady}
      >
        <Button
          label="Open Portal"
          suffixIcon="ArrowNE"
          variant="secondary"
          size="sm"
          className="rounded-full bg-transparent"
          disabled={!isPortalReady}
          onClick={handleClick}
        />
      </Tooltip>
      <PortalInterceptModal
        isOpen={isOpen}
        onClose={dismiss}
        onGoToCrm={goToCrm}
        onContinueToPortal={handleContinueToPortal}
      />
    </>
  );
};

export const SidebarBottomBar = ({
  onSettingsClick,
  onHelpCenterClick,
  isClient,
  isHelpCenterOpen,
  isSettingsSelected,
}: SidebarBottomBarProps) => {
  const {
    sidebarTheme: { sidebarTextColor, sidebarItemHoverBackground },
  } = useSidebarTheme();
  const clientSidebarStyles = {
    color: sidebarTextColor,
    borderColor: 'transparent',
    '--sidebar-hover-bg': sidebarItemHoverBackground,
  } as CSSProperties;

  return (
    <div className="flex gap-2 px-1 py-1">
      <Tooltip
        content="Settings"
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        baseOffset={4}
        showDelayDuration={TOOLTIP_DELAY}
        tooltipClassname={tooltipClassname}
      >
        <div aria-current={isSettingsSelected ? 'page' : undefined}>
          <IconButton
            icon="Settings"
            aria-label="Settings"
            variant="secondary"
            size="sm"
            className={`rounded-full bg-transparent ${isClient ? '[color:var(--sidebar-text)] hover:!bg-[var(--sidebar-hover-bg)]' : ''}`}
            style={isClient ? clientSidebarStyles : undefined}
            onClick={onSettingsClick}
          />
        </div>
      </Tooltip>
      {!isClient && (
        <Tooltip
          content={HELP_CENTER_PAGE.label}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          baseOffset={4}
          showDelayDuration={TOOLTIP_DELAY}
          tooltipClassname={tooltipClassname}
        >
          <div aria-expanded={isHelpCenterOpen}>
            <IconButton
              icon="QuestionMark"
              aria-label="Help Center"
              variant="secondary"
              size="sm"
              className="rounded-full bg-transparent"
              onClick={onHelpCenterClick}
            />
          </div>
        </Tooltip>
      )}
      {!isClient && (
        <div className="ml-auto">
          <PortalButton />
        </div>
      )}
    </div>
  );
};
